<script setup lang="ts">
import { computed } from 'vue';

import { assertValue } from '@libs/utils';

import AppButton from '../../appButton/AppButton.vue';
import AppIconButton from '../../appIconButton/AppIconButton.vue';
import AppRawButton from '../../appRawButton/AppRawButton.vue';

import { GalleryButtonType } from './AppMasonryGallery.model';
import type {
  AppMasonryGalleryOrientation,
  GalleryButton,
} from './AppMasonryGallery.model';

interface AppMasonryGalleryProps {
  imageUrls: string[];
  altImageText: string;
  orientation: AppMasonryGalleryOrientation;
  galleryButton: GalleryButton;
}

const props = defineProps<AppMasonryGalleryProps>();

interface AppMasonryGalleryEmits {
  (eventName: 'show-full-gallery'): void;
}

const emit = defineEmits<AppMasonryGalleryEmits>();

const THREE_IMAGES = 3;
const FOUR_IMAGES = 4;
const FIVE_IMAGES = 5;

const visibleImageUrls = computed(() => props.imageUrls.slice(0, FIVE_IMAGES));

const layoutClass = computed(() => {
  const imagesTotal = props.imageUrls.length;

  assertValue(
    props.imageUrls[0],
    'AppMansoryGallery.vue > layoutClass > props.imageUrls[0]'
  );

  if (imagesTotal === 1) {
    return 'layout-one';
  }

  if (imagesTotal === 2) {
    return 'layout-two';
  }

  if (imagesTotal === THREE_IMAGES) {
    return 'layout-three';
  }

  if (imagesTotal === FOUR_IMAGES) {
    return 'layout-four';
  }

  return 'layout-five';
});

function getBackgroundImageStyle(imageUrl: string): string {
  return `background-image: url('${imageUrl}');`;
}

function getImageAriaLabel(index: number): string {
  return `${props.altImageText} ${index}`;
}

function onClick(): void {
  if (props.galleryButton.type !== GalleryButtonType.NoButton) {
    emit('show-full-gallery');
  }
}
</script>

<template>
  <div
    class="app-masonry-gallery"
    :class="[
      props.orientation,
      layoutClass,
      {
        'allow-show-full-gallery-action':
          props.galleryButton.type !== GalleryButtonType.NoButton,
      },
    ]"
  >
    <template v-for="(imageUrl, index) in visibleImageUrls" :key="index">
      <AppRawButton
        class="image-tile"
        :class="`image-${index}`"
        :aria-label="getImageAriaLabel(index)"
        :style="getBackgroundImageStyle(imageUrl)"
        @click="onClick"
      />
    </template>

    <AppButton
      v-if="props.galleryButton.type === GalleryButtonType.TextAndIconButton"
      type="secondary"
      state="enabled"
      size="medium"
      :left-icon="{ name: 'masonryGallery' }"
      class="show-gallery-button"
      @click="onClick"
    >
      {{ props.galleryButton.text }}
    </AppButton>

    <AppIconButton
      v-else-if="props.galleryButton.type === GalleryButtonType.IconButton"
      type="secondary"
      state="enabled"
      size="medium"
      :icon="{ name: 'masonryGallery' }"
      class="show-gallery-button"
      :ariaLabel="props.galleryButton.ariaLabel"
      @click="onClick"
    />
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/media' as *;

@mixin image-hover {
  @include on-hover {
    cursor: pointer;
    zoom: 1;
    filter: alpha(opacity=80);
    opacity: 0.8;
  }
}

.app-masonry-gallery {
  width: 100%;
  height: 100%;
  display: grid;
  gap: var(--spacing-8);
  border-radius: var(--border-radius-8);
  position: relative;

  &.allow-show-full-gallery-action {
    .image-tile {
      transition: opacity var(--transition-medium);
      @include image-hover;
    }
  }

  .image-tile {
    all: unset;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    width: 100%;
    height: 100%;
  }

  .show-gallery-button {
    position: absolute;
    bottom: var(--spacing-16);
    right: var(--spacing-16);
    @include on-hover {
      cursor: pointer;
    }
  }

  &.horizontal {
    &.layout-one {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;

      .image-0 {
        border-radius: var(--spacing-12);
        grid-column: 1 / -1;
        grid-row: 1 / -1;
      }
    }

    &.layout-two {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr;

      .image-0 {
        grid-column: 1/2;
        border-top-left-radius: var(--spacing-12);
        border-bottom-left-radius: var(--spacing-12);
      }

      .image-1 {
        grid-column: 2/3;
        border-top-right-radius: var(--spacing-12);
        border-bottom-right-radius: var(--spacing-12);
      }
    }

    &.layout-three {
      grid-template-columns: 2fr 1fr;
      grid-template-rows: 1fr 1fr;

      .image-0 {
        grid-column: 1/2;
        grid-row: 1/3;
        border-top-left-radius: var(--spacing-12);
        border-bottom-left-radius: var(--spacing-12);
      }

      .image-1 {
        grid-column: 2/3;
        grid-row: 1/2;
        border-top-right-radius: var(--spacing-12);
      }

      .image-2 {
        grid-column: 2/3;
        grid-row: 2/3;
        border-bottom-right-radius: var(--spacing-12);
      }
    }

    &.layout-four {
      grid-template-columns: 2fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;

      .image-0 {
        grid-column: 1/2;
        grid-row: 1 / -1;
        border-top-left-radius: var(--spacing-12);
        border-bottom-left-radius: var(--spacing-12);
      }

      .image-1 {
        grid-column: 2/3;
        grid-row: 1/2;
      }

      .image-2 {
        grid-column: 3/4;
        grid-row: 1/2;
        border-top-right-radius: var(--spacing-12);
      }

      .image-3 {
        grid-column: 2/4;
        grid-row: 2/3;
        border-bottom-right-radius: var(--spacing-12);
      }
    }

    &.layout-five {
      grid-template-columns: 2fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;

      .image-0 {
        grid-column: 1/2;
        grid-row: 1/-1;
        border-top-left-radius: var(--spacing-12);
        border-bottom-left-radius: var(--spacing-12);
      }

      .image-1 {
        grid-column: 2/3;
        grid-row: 1/2;
      }

      .image-2 {
        grid-column: 3/4;
        grid-row: 1/2;
        border-top-right-radius: var(--spacing-12);
      }

      .image-3 {
        grid-column: 2/3;
        grid-row: 2/3;
      }

      .image-3 {
        grid-column: 3/4;
        grid-row: 2/3;
        border-bottom-right-radius: var(--spacing-12);
      }
    }
  }

  &.vertical {
    &.layout-one {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;

      .image-0 {
        border-radius: var(--spacing-12);
        grid-column: 1 / -1;
        grid-row: 1 / -1;
      }
    }

    &.layout-two {
      grid-template-rows: 1fr 1fr;
      grid-template-columns: 1fr;

      .image-0 {
        grid-row: 1/2;
      }

      .image-1 {
        grid-row: 2/3;
      }
    }

    &.layout-three {
      grid-template-rows: 2fr 1fr;
      grid-template-columns: 1fr 1fr;

      .image-0 {
        grid-row: 1/2;
        grid-column: 1/3;
      }

      .image-1 {
        grid-row: 2/3;
        grid-column: 1/2;
      }

      .image-2 {
        grid-row: 2/3;
        grid-column: 2/3;
      }
    }

    &.layout-four {
      grid-template-rows: 2fr 1fr 1fr;
      grid-template-columns: 1fr 1fr;

      .image-0 {
        grid-row: 1/2;
        grid-column: 1 / -1;
      }

      .image-1 {
        grid-row: 2/3;
        grid-column: 1/2;
      }

      .image-2 {
        grid-row: 3/4;
        grid-column: 1/2;
      }

      .image-3 {
        grid-row: 2/4;
        grid-column: 2/3;
      }
    }

    &.layout-five {
      grid-template-rows: 2fr 1fr 1fr;
      grid-template-columns: 1fr 1fr;

      .image-0 {
        grid-row: 1/2;
        grid-column: 1/-1;
      }

      .image-1 {
        grid-row: 2/3;
        grid-column: 1/2;
      }

      .image-2 {
        grid-row: 3/4;
        grid-column: 1/2;
      }

      .image-3 {
        grid-row: 2/3;
        grid-column: 2/3;
      }

      .image-3 {
        grid-row: 3/4;
        grid-column: 2/3;
      }
    }
  }
}
</style>
